import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import {
  CODE_URL_KINDS,
  type ActionItemCode,
  type ActionItemCodeUrlKind
} from "../lib/actionItems.js";

/**
 * Tenant admin — Action Item Codes (ADR-0053).
 * Spec: governance-foundation §19.6.
 *
 * Route: `/app/admin/action-item-codes?kind=<urlKind>`
 *
 * Tabbed CRUD for the five code tables. Includes inactive rows so admins
 * can re-activate previously retired codes.
 */

import {
  deactivationBlockedMessage,
  deactivationBlockedReason,
  deactivationRefusalMessage,
} from "../lib/actionItemCodeGuards.js";

function urlKindFromSearch(raw: string | null): ActionItemCodeUrlKind {
  const valid = CODE_URL_KINDS.find((k) => k.urlKind === raw);
  return valid?.urlKind ?? "item-types";
}

type DraftCode = {
  code: string;
  label: string;
  description: string;
  sort_order: string;
};

const EMPTY_DRAFT: DraftCode = { code: "", label: "", description: "", sort_order: "" };

export function AdminActionItemCodes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlKind = urlKindFromSearch(searchParams.get("kind"));
  const toast = useToast();
  const { confirm } = useConfirm();
  /** ADR-0161 — spliced into the guard sentences so they name the kind. */
  const kindLabel = CODE_URL_KINDS.find((k) => k.urlKind === urlKind)?.label ?? "code";

  const setKind = useCallback(
    (next: ActionItemCodeUrlKind) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set("kind", next);
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [codes, setCodes] = useState<ActionItemCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-row edit state — keyed by code.id.
  const [editing, setEditing] = useState<Record<string, DraftCode>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  // New row
  const [newOpen, setNewOpen] = useState(false);
  const [newDraft, setNewDraft] = useState<DraftCode>(EMPTY_DRAFT);
  const [newBusy, setNewBusy] = useState(false);
  const [newError, setNewError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ codes: ActionItemCode[] }>(
        `/api/tenant/admin/action-item-codes/${urlKind}?include_inactive=true`
      );
      setCodes(res.codes);
      setEditing({});
      setRowError({});
      setBusy({});
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [urlKind]);

  useEffect(() => {
    void load();
    setNewOpen(false);
    setNewDraft(EMPTY_DRAFT);
    setNewError(null);
  }, [load]);

  function beginEdit(c: ActionItemCode) {
    setEditing((prev) => ({
      ...prev,
      [c.id]: {
        code: c.code,
        label: c.label,
        description: c.description ?? "",
        sort_order: String(c.sort_order)
      }
    }));
    setRowError((prev) => ({ ...prev, [c.id]: "" }));
  }

  function cancelEdit(id: string) {
    setEditing((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
    setRowError((prev) => ({ ...prev, [id]: "" }));
  }

  function updateDraft(id: string, patch: Partial<DraftCode>) {
    setEditing((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }

  async function save(c: ActionItemCode) {
    const draft = editing[c.id];
    if (!draft) return;
    if (!draft.label.trim()) {
      setRowError((prev) => ({ ...prev, [c.id]: "Label is required." }));
      return;
    }
    setBusy((prev) => ({ ...prev, [c.id]: true }));
    setRowError((prev) => ({ ...prev, [c.id]: "" }));
    try {
      const sortOrder = draft.sort_order === "" ? undefined : Number(draft.sort_order);
      await apiFetch(`/api/tenant/admin/action-item-codes/${urlKind}/${c.id}`, {
        method: "PATCH",
        body: {
          label: draft.label.trim(),
          description: draft.description.trim() || null,
          ...(Number.isFinite(sortOrder) ? { sort_order: sortOrder } : {})
        }
      });
      toast.push({ kind: "success", message: "Code updated." });
      await load();
    } catch (e) {
      setRowError((prev) => ({
        ...prev,
        [c.id]: e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e)
      }));
    } finally {
      setBusy((prev) => ({ ...prev, [c.id]: false }));
    }
  }

  async function deactivate(c: ActionItemCode) {
    const ok = await confirm({
      title: `Deactivate "${c.label}"?`,
      detail: "If this code is referenced by any open action item, the hub will block the deactivation.",
      confirmLabel: "Deactivate"
    });
    if (!ok) return;
    setBusy((prev) => ({ ...prev, [c.id]: true }));
    setRowError((prev) => ({ ...prev, [c.id]: "" }));
    try {
      await apiFetch(`/api/tenant/admin/action-item-codes/${urlKind}/${c.id}`, { method: "DELETE" });
      toast.push({ kind: "success", message: "Code deactivated." });
      await load();
    } catch (e) {
      // ADR-0161 — the hub now refuses three ways (in-use, reserved, floor).
      // Unmapped codes fall through to the server's own message: a vague
      // friendly error is worse than the raw one.
      const msg =
        e instanceof ApiRequestError
          ? deactivationRefusalMessage(e.code, kindLabel) ?? e.message
          : e instanceof Error
          ? e.message
          : String(e);
      setRowError((prev) => ({ ...prev, [c.id]: msg }));
    } finally {
      setBusy((prev) => ({ ...prev, [c.id]: false }));
    }
  }

  async function reactivate(c: ActionItemCode) {
    setBusy((prev) => ({ ...prev, [c.id]: true }));
    setRowError((prev) => ({ ...prev, [c.id]: "" }));
    try {
      await apiFetch(`/api/tenant/admin/action-item-codes/${urlKind}/${c.id}`, {
        method: "PATCH",
        body: { active: true }
      });
      toast.push({ kind: "success", message: "Code reactivated." });
      await load();
    } catch (e) {
      setRowError((prev) => ({
        ...prev,
        [c.id]: e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e)
      }));
    } finally {
      setBusy((prev) => ({ ...prev, [c.id]: false }));
    }
  }

  async function createNew() {
    if (!newDraft.code.trim() || !newDraft.label.trim()) {
      setNewError("Code and label are required.");
      return;
    }
    setNewBusy(true);
    setNewError(null);
    try {
      const sortOrder = newDraft.sort_order === "" ? undefined : Number(newDraft.sort_order);
      await apiFetch(`/api/tenant/admin/action-item-codes/${urlKind}`, {
        method: "POST",
        body: {
          code: newDraft.code.trim(),
          label: newDraft.label.trim(),
          description: newDraft.description.trim() || null,
          ...(Number.isFinite(sortOrder) ? { sort_order: sortOrder } : {})
        }
      });
      toast.push({ kind: "success", message: "Code created." });
      setNewDraft(EMPTY_DRAFT);
      setNewOpen(false);
      await load();
    } catch (e) {
      const msg =
        e instanceof ApiRequestError
          ? e.code === "code_exists"
            ? "A code with this value already exists."
            : e.message
          : e instanceof Error
          ? e.message
          : String(e);
      setNewError(msg);
    } finally {
      setNewBusy(false);
    }
  }

  return (
    <PageLayout title="Action Item Codes">
      <div style={{ display: "grid", gap: 12 }}>
        <div className="tabs">
          {CODE_URL_KINDS.map((k) => (
            <button
              key={k.urlKind}
              type="button"
              className={`tab${urlKind === k.urlKind ? " tabActive" : ""}`}
              onClick={() => setKind(k.urlKind)}
            >
              {k.label}
            </button>
          ))}
        </div>

        <div className="card">
          <div className="toolbar" style={{ marginBottom: 10 }}>
            <div className="toolbarGroup" style={{ marginLeft: "auto" }}>
              {!newOpen && (
                <button className="btn btnPrimary" onClick={() => setNewOpen(true)}>
                  + Add code
                </button>
              )}
            </div>
          </div>

          {newOpen && (
            <div className="card" style={{ marginBottom: 12, background: "var(--bg-subtle)" }}>
              <div style={{ display: "grid", gap: 10 }}>
                <div className="inputRow" style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: 10 }}>
                  <label className="field">
                    <span className="label">Code *</span>
                    <input
                      className="input mono"
                      value={newDraft.code}
                      onChange={(e) => setNewDraft((d) => ({ ...d, code: e.target.value }))}
                      placeholder="short_slug"
                    />
                  </label>
                  <label className="field">
                    <span className="label">Label *</span>
                    <input
                      className="input"
                      value={newDraft.label}
                      onChange={(e) => setNewDraft((d) => ({ ...d, label: e.target.value }))}
                      placeholder="Human-readable label"
                    />
                  </label>
                  <label className="field">
                    <span className="label">Sort order</span>
                    <input
                      type="number"
                      className="input"
                      value={newDraft.sort_order}
                      onChange={(e) => setNewDraft((d) => ({ ...d, sort_order: e.target.value }))}
                      placeholder="0"
                    />
                  </label>
                </div>
                <label className="field">
                  <span className="label">Description</span>
                  <input
                    className="input"
                    value={newDraft.description}
                    onChange={(e) => setNewDraft((d) => ({ ...d, description: e.target.value }))}
                    placeholder="Optional"
                  />
                </label>
                {newError && <div className="help" style={{ color: "var(--danger-text)" }}>{newError}</div>}
                <div className="actions">
                  <button
                    className="btn"
                    onClick={() => {
                      setNewOpen(false);
                      setNewDraft(EMPTY_DRAFT);
                      setNewError(null);
                    }}
                    disabled={newBusy}
                  >
                    Cancel
                  </button>
                  <button className="btn btnPrimary" onClick={() => void createNew()} disabled={newBusy}>
                    {newBusy ? "Creating…" : "Create"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {error && <ErrorState detail={error} />}
          {loading && !error && <LoadingState />}
          {!loading && !error && codes.length === 0 && (
            <div className="adminListEmpty">No codes defined yet.</div>
          )}
          {!loading && !error && codes.length > 0 && (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Label</th>
                    <th>Description</th>
                    <th>Sort</th>
                    <th>Status</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c) => {
                    const isEditing = Boolean(editing[c.id]);
                    const draft = editing[c.id];
                    const rowBusy = busy[c.id] ?? false;
                    const err = rowError[c.id];
                    // ADR-0161 §7.3 — reasons the client can already see this
                    // deactivation would be refused. Conservative: in-use is not
                    // knowable from the list, so it always arrives as a refusal.
                    const blockedReason = deactivationBlockedReason(c, codes);
                    return (
                      <tr key={c.id}>
                        <td className="mono">{c.code}</td>
                        <td>
                          {isEditing ? (
                            <input
                              className="input"
                              value={draft.label}
                              onChange={(e) => updateDraft(c.id, { label: e.target.value })}
                            />
                          ) : (
                            c.label
                          )}
                        </td>
                        <td style={{ maxWidth: 360 }}>
                          {isEditing ? (
                            <input
                              className="input"
                              value={draft.description}
                              onChange={(e) => updateDraft(c.id, { description: e.target.value })}
                            />
                          ) : (
                            <span className="muted">{c.description ?? ""}</span>
                          )}
                        </td>
                        <td style={{ width: 80 }}>
                          {isEditing ? (
                            <input
                              type="number"
                              className="input"
                              value={draft.sort_order}
                              onChange={(e) => updateDraft(c.id, { sort_order: e.target.value })}
                            />
                          ) : (
                            c.sort_order
                          )}
                        </td>
                        <td>
                          {c.active ? (
                            <span className="pill pillOk">active</span>
                          ) : (
                            <span className="pill pillBad">inactive</span>
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {/*
                            A disabled button with no stated reason is a dead
                            end. The reason is rendered here rather than in a
                            title tooltip so it is readable without hovering.
                          */}
                          {!err && blockedReason && (
                            <div className="help" style={{ textAlign: "right", marginBottom: 4 }}>
                              {deactivationBlockedMessage(blockedReason, kindLabel)}
                            </div>
                          )}
                          {err && (
                            <div className="help" style={{ color: "var(--danger-text)", textAlign: "right", marginBottom: 4 }}>
                              {err}
                            </div>
                          )}
                          <div className="adminListActions">
                            {isEditing ? (
                              <>
                                <button
                                  className="adminListAction"
                                  onClick={() => void save(c)}
                                  disabled={rowBusy}
                                >
                                  Save
                                </button>
                                <button
                                  className="adminListAction"
                                  onClick={() => cancelEdit(c.id)}
                                  disabled={rowBusy}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="adminListAction"
                                  onClick={() => beginEdit(c)}
                                  disabled={rowBusy}
                                >
                                  Edit
                                </button>
                                {c.active ? (
                                  <button
                                    className="adminListAction isDanger"
                                    onClick={() => void deactivate(c)}
                                    disabled={rowBusy || blockedReason !== null}
                                  >
                                    Deactivate
                                  </button>
                                ) : (
                                  <button
                                    className="adminListAction"
                                    onClick={() => void reactivate(c)}
                                    disabled={rowBusy}
                                  >
                                    Reactivate
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
